/* ChicChac — cart, founder change-set v3. Hardened 2026-08-16 (A4-002, A4-003,
   A8-009 from the 2026-08-15 audit).

   Two jobs:

   1. ONE checkout flow. The store shipped with two: the slide-in drawer had its
      own Checkout button (plus Razorpay's accelerated button) and so did the
      dedicated /cart page. Founder's call — the drawer becomes a view of what's
      in the bag and nothing more; every pre-checkout action (quantities are
      fine, but discount codes, notes, and the checkout itself) happens on the
      cart page. So the drawer's checkout controls are suppressed and the note/
      discount accordion is removed, replaced with a single link through to
      /cart. The /cart page's own #checkout button is untouched — that IS the
      one real flow (Razorpay Magic Checkout intercepts what happens on click).

      A8-009 fix: the checkout button/accelerated-buttons suppression used to
      match Horizon's BEM classnames (`.cart__ctas`) and `.remove()` the node —
      so a Horizon markup rename silently let the button back in, and a removed
      node can't be un-removed if Horizon later re-renders around it. It now
      matches by Shopify's own stable contract instead: `name="checkout"` is
      the attribute Shopify's cart form itself depends on to submit, and
      `.shopify-payment-button` is Shopify's own wrapper class for the
      accelerated buttons (Shop Pay / PayPal / G Pay), injected by the platform,
      not by Horizon — both survive a theme update that renames Horizon's own
      classes. Suppression is disable+hide, not removal: a `disabled` button is
      unfocusable and unsubmittable even if some later stylesheet fights the
      inline `display:none`, and hiding (not deleting) means the same node is
      still there to re-suppress the next time this runs — see run() below,
      called after every real cart-mutation event, so this is self-healing
      against Horizon's own re-renders rather than a one-shot removal.

   2. A free-shipping meter on both surfaces, since ₹499 is the store's main
      basket-building lever and nothing surfaced it before. Its amounts are
      read from theme settings via window.CC (see layout/theme.liquid) — the
      theme states a shipping or COD figure in exactly one place.

   A4-002/A4-003 fix: this file used to guess at Horizon's cart-update event
   names ('cart:refresh', 'cart:update', 'CartUpdate' — none of which Horizon
   actually dispatches) and fall back to a document-wide MutationObserver that
   re-fetched /cart.js on every DOM mutation anywhere on the page, which is why
   a single page load made 7-11 GET /cart.js calls. Horizon's real contract
   (verified in theme-horizon-dev/assets/component-cart-items.js,
   cart-discount.js, cart-icon.js and the standard-events.js module they all
   import from '@shopify/events') is `shopify:cart:lines-update` and
   `shopify:cart:discount-update` — bubbling DOM events dispatched on document
   by every cart mutator (add, quantity change, remove, discount apply), each
   carrying `event.promise`, which resolves with the fresh cart totals AFTER
   Horizon has already morphed the drawer/summary markup back in. So this file
   now fetches /cart.js exactly once, on page load, to seed the meter; every
   mutation after that is read off event.promise — zero extra fetches, zero
   polling. cart-icon.js listens the same way for the header bubble, so this
   is proven-in-theme, not invented.
*/
(function () {
  'use strict';

  var CC = window.CC || {};
  /* Both amounts come from Theme settings → ChicChac → "Shipping & COD", which
     layout/theme.liquid renders onto window.CC in paise. The literals below are
     a last-resort fallback for the case where this file runs without that
     inline script; they are NOT a second place to edit the numbers. */
  var THRESHOLD = typeof CC.freeShipThreshold === 'number' ? CC.freeShipThreshold : 49900;
  var COD_CHARGE = typeof CC.codCharge === 'number' ? CC.codCharge : 4900; /* ₹49 — 8900 until 2026-08-11, 5900 until 2026-08-13 */

  var TRUCK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z"/><circle cx="7" cy="18" r="1.6"/><circle cx="17.5" cy="18" r="1.6"/></svg>';

  /* cart.total_price is in the shop's minor unit. INR has no sub-rupee pricing
     on this catalogue, so round to whole rupees — "₹79 away" beats "₹79.00". */
  function rupees(paise) {
    return '₹' + Math.ceil(paise / 100).toLocaleString('en-IN');
  }

  /* ---------------- free-shipping meter ---------------- */

  /* Founder change-set v4b: the meter no longer carries the COD sentence.
     NOTE for whoever reads this next — the threshold only frees PREPAID
     shipping. The Cash-on-Delivery rate is flat ₹49 above AND below ₹499,
     because a Shopify rate can key off the order price or be COD-only but not
     both. So "Free shipping" unqualified is a false claim for a COD customer,
     who still pays ₹49 at any basket size — A5-004 on the 2026-08-15 audit.
     2026-08-16: both branches now say "prepaid", matching the announcement
     bar's corrected copy (theme-live/sections/header-group.json, Lane A) and
     the Shipping Policy. The COD figure itself is still stated only in the
     cart's payment-assurance line (see reassure() below), not here. */
  function meterMarkup(total) {
    var left = THRESHOLD - total;
    var pct = Math.max(0, Math.min(100, (total / THRESHOLD) * 100));
    var done = left <= 0;
    return {
      done: done,
      pct: done ? 100 : pct,
      text: done
        ? 'Free prepaid shipping on orders above ' + rupees(THRESHOLD) + '.'
        : "You're " + rupees(left) + ' away from free prepaid shipping.'
    };
  }

  function renderMeter(host, total) {
    var m = meterMarkup(total);
    var el = host.querySelector('.cc-shipmeter');
    if (!el) {
      el = document.createElement('div');
      el.className = 'cc-shipmeter';
      el.innerHTML =
        '<div class="cc-shipmeter__row">' +
          '<span class="cc-shipmeter__icon">' + TRUCK + '</span>' +
          '<p class="cc-shipmeter__text" role="status"></p>' +
        '</div>' +
        '<div class="cc-shipmeter__track"><span class="cc-shipmeter__fill"></span></div>';
      host.insertBefore(el, host.firstChild);
    }
    el.classList.toggle('is-done', m.done);
    /* Only write when the value actually changed. There's no MutationObserver
       driving this anymore (that was the A4-003 polling bug), but this stays
       cheap-and-idempotent regardless — renderMeter() can be called from
       runStructural() on any cart event, event vs event, without redoing DOM
       writes that didn't change anything. */
    var t = el.querySelector('.cc-shipmeter__text');
    if (t.textContent !== m.text) t.textContent = m.text;
    var w = m.pct + '%';
    var fill = el.querySelector('.cc-shipmeter__fill');
    if (fill.style.width !== w) fill.style.width = w;
  }

  /* Mount inside `.cart-summary__inner`, not the outer `.cart-page__summary`.
     The outer element is a CSS grid whose only real child is explicitly placed
     at `grid-row: 1 / -1`; an auto-placed sibling therefore lands in a row
     *after* it — which is how the meter ended up dangling below the Check out
     button and outside the card. */
  function meterHosts() {
    var hosts = [];
    ['.cart-drawer__summary', '.cart-page__summary'].forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (outer) {
        hosts.push(outer.querySelector('.cart-summary__inner') ||
                   outer.querySelector('.cart-summary') || outer);
      });
    });
    return hosts;
  }

  /* Paints the meter into every mount point from an already-known cart state —
     no fetch. `totalPaise` / `itemCount` come either from the one-time page-load
     read of /cart.js (seedMeterFromCartJs(), called from run() below) or from a
     cart-mutation event's event.promise (onCartMutation() below). */
  function applyMeter(totalPaise, itemCount) {
    var hosts = meterHosts();
    hosts.forEach(function (h) {
      /* An empty bag has nothing to make progress toward — drop the meter
         rather than showing "₹499 away" over a cart with no items. */
      var existing = h.querySelector('.cc-shipmeter');
      if (!itemCount) { if (existing) existing.remove(); return; }
      renderMeter(h, totalPaise);
    });
  }

  /* The ONE /cart.js call per page load. Every update after this comes from
     Horizon's own cart events (wireCartEvents below), never a re-fetch. */
  function seedMeterFromCartJs() {
    if (!meterHosts().length) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { applyMeter(c.total_price, c.item_count); })
      .catch(function () { /* meter is decorative — never block the cart on it */ });
  }

  /* ---------------- payment assurance ----------------
     Founder change-set v4b: "safe payments managed by razorpay (adv somewhere
     in checkout)". Sits directly under the Check out button, which is where a
     hesitant COD-market shopper actually looks for it. This is also the only
     place the flat COD charge is now stated, since it came out of the shipping
     meter — a customer choosing COD still pays it at any basket size. */
  var LOCK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">' +
    '<rect x="4.5" y="10.5" width="15" height="10" rx="2.5"/>' +
    '<path d="M8.2 10.5V7.6a3.8 3.8 0 0 1 7.6 0v2.9" stroke-linecap="round"/></svg>';

  function reassure() {
    document.querySelectorAll('.cart-page__summary, .cart-drawer__summary').forEach(function (outer) {
      var host = outer.querySelector('.cart-summary__inner') ||
                 outer.querySelector('.cart-summary') || outer;
      if (host.querySelector('.cc-assure')) return;
      var el = document.createElement('div');
      el.className = 'cc-assure';
      el.innerHTML =
        '<span class="cc-assure__icon">' + LOCK + '</span>' +
        '<p class="cc-assure__text">Secure checkout — safe payments managed by Razorpay. ' +
        'UPI, cards, net banking, wallets and Cash on Delivery.' +
        (COD_CHARGE > 0 ? ' COD adds a flat ' + rupees(COD_CHARGE) + '.' : '') +
        '</p>';
      host.appendChild(el);
    });
  }

  /* ---------------- drawer → view only ---------------- */

  /* A8-009: suppress by Shopify's own stable contract, not Horizon's BEM
     classnames — see the file header for why. Disable AND hide: `disabled`
     makes the control unfocusable/unsubmittable even if a future stylesheet
     out-specifies the inline display:none, and hiding (not removing) the node
     means the very next run() re-applies cleanly instead of finding nothing
     to suppress. Scoped to `.cart-drawer__summary` only — the /cart page's
     own #checkout button (same "name=checkout" attribute) is the one real
     checkout flow and must never match this selector. */
  function suppressDrawerCheckout(sum) {
    sum.querySelectorAll('[name="checkout"], .shopify-payment-button, .additional-checkout-buttons')
      .forEach(function (el) {
        if ('disabled' in el) el.disabled = true;
        el.setAttribute('aria-hidden', 'true');
        el.setAttribute('tabindex', '-1');
        el.style.display = 'none';
      });
  }

  function demoteDrawer() {
    document.querySelectorAll('.cart-drawer__summary').forEach(function (sum) {
      /* Re-runs on every drawer re-render, so this has to be idempotent AND
         re-apply to freshly rendered markup — check for the link, not a flag.
         The note/discount accordion isn't a checkout path (just clutter the
         founder wants gone from the drawer), so it's still a plain removal. */
      sum.querySelectorAll('.cart-actions').forEach(function (n) { n.remove(); });
      suppressDrawerCheckout(sum);

      if (sum.querySelector('.cc-viewcart')) return;
      var a = document.createElement('a');
      a.className = 'cc-viewcart';
      a.href = '/cart';
      a.textContent = 'View bag & checkout';
      sum.appendChild(a);
    });
  }

  /* ---------------- cart page extras ---------------- */

  function cartPageExtras() {
    var page = document.querySelector('.cart-page');
    if (!page || page.classList.contains('cart-page--empty')) return;
    var items = page.querySelector('.cart-page__items');
    if (!items || items.querySelector('.cc-keepshopping')) return;
    var a = document.createElement('a');
    a.className = 'cc-keepshopping';
    a.href = '/collections/new-arrivals';
    a.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true">' +
      '<path d="M20 12H5M11 6l-6 6 6 6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    var label = document.createElement('span');
    label.textContent = 'Continue shopping';
    a.appendChild(label);
    items.appendChild(a);
  }

  /* Structural re-injection — no network call, safe to run as often as needed. */
  function runStructural() {
    demoteDrawer();
    cartPageExtras();
    reassure();
  }

  /* ---------------- Horizon's real cart-update contract ----------------
     `shopify:cart:lines-update` / `shopify:cart:discount-update` are dispatched
     on `document` (bubbling) by every cart mutator in this theme — add,
     quantity change, remove, discount apply — see component-cart-items.js and
     cart-discount.js in theme-horizon-dev/assets/, both importing from
     '@shopify/events'. Their `event.promise` resolves AFTER Horizon has already
     morphed the drawer/summary markup back into the DOM (the resolve() call and
     the morphSection() call happen back-to-back, synchronously, inside the same
     fetch .then() — our promise .then() is a microtask queued behind both), so
     by the time this fires the fresh nodes already exist and are safe to
     re-suppress/re-decorate. `cart-note` updates are excluded on purpose: that
     mutator never morphs the summary (grepped theme-horizon-dev/assets/cart-
     note.js — no morphSection call), so there's nothing here for it to re-do. */
  function onCartMutation(event) {
    if (!event.promise) { runStructural(); return; }
    event.promise
      .then(function (result) {
        runStructural();
        var cart = result && result.cart;
        var detail = result && result.detail;
        if (!cart) return;
        var itemCount = typeof cart.totalQuantity === 'number'
          ? cart.totalQuantity
          : (detail && detail.itemCount) || 0;
        /* cart.cost.totalAmount.amount is decimal rupees (Horizon's own
           fromAjaxCart() divides total_price by 100) — meterMarkup/rupees()
           work in paise, same unit /cart.js returns, so convert back once here
           rather than carrying two units through the rest of the file. */
        var totalPaise = Math.round(parseFloat(cart.cost.totalAmount.amount) * 100);
        applyMeter(totalPaise, itemCount);
      })
      .catch(function () {
        /* Cart error (e.g. stock ran out mid-update) — no new totals to paint,
           but the suppression/decoration must still hold on whatever DOM is
           left, in case Horizon re-rendered before rejecting. */
        runStructural();
      });
  }

  function run() {
    runStructural();
    seedMeterFromCartJs(); /* the one and only /cart.js call for this page load */
  }

  if (document.readyState !== 'loading') run();
  else document.addEventListener('DOMContentLoaded', run);

  ['shopify:cart:lines-update', 'shopify:cart:discount-update'].forEach(function (ev) {
    document.addEventListener(ev, onCartMutation);
  });
})();
